import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Tabs,
  Tab,
  
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  Chip
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  Fullscreen as FullscreenIcon,
  Close as CloseIcon,
  Home as HomeIcon,
  Straighten as StraightenIcon
} from '@mui/icons-material';
import { FloorPlans, FloorPlanData } from '../types/api.types';

interface FloorPlanViewerProps {
  plans: FloorPlans;
}

const FloorPlanViewer: React.FC<FloorPlanViewerProps> = ({ plans }) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [fullscreenPlan, setFullscreenPlan] = useState<{ type: string; data: FloorPlanData } | null>(null);

  // Handle both array and object formats
  const availablePlans: Array<[string, FloorPlanData]> = Array.isArray(plans) 
    ? plans.map((plan, index) => [plan.scenario || `Floor ${plan.floor_number || index + 1}`, plan])
    : Object.entries(plans).filter(([_, data]) => data !== undefined);
  
  const planTypes = availablePlans.map(([type, _]) => type);
  const currentPlan = availablePlans[selectedTab]?.[1];

  const formatArea = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getPlanTypeLabel = (type: string) => {
    switch (type) {
      case 'renovation':
        return 'Renovation Plan';
      case 'extension':
        return 'Extension Plan';
      case 'teardown':
        return 'Teardown & Rebuild Plan';
      default:
        return type.charAt(0).toUpperCase() + type.slice(1) + ' Plan';
    }
  };

  const handleFullscreen = () => {
    if (currentPlan) {
      setFullscreenPlan({ type: planTypes[selectedTab], data: currentPlan });
    }
  };

  if (availablePlans.length === 0) {
    return null;
  }

  return (
    <>
      <Paper sx={{ p: 4, borderRadius: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Floor Plans & Layouts
        </Typography>

        <Tabs
          value={selectedTab}
          onChange={(_, newValue) => setSelectedTab(newValue)}
          sx={{ mb: 3 }}
        >
          {planTypes.map((type) => (
            <Tab key={type} label={getPlanTypeLabel(type)} />
          ))}
        </Tabs>

        {currentPlan && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card sx={{ position: 'relative' }}>
                <CardMedia
                  component="div"
                  sx={{
                    height: 500,
                    bgcolor: 'grey.100',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative'
                  }}
                >
                  <Box
                    dangerouslySetInnerHTML={{ __html: currentPlan.svg || currentPlan.svg_layout || '' }}
                    sx={{
                      width: '100%',
                      height: '100%',
                      '& svg': {
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain'
                      }
                    }}
                  />
                  <IconButton
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      bgcolor: 'background.paper',
                      '&:hover': {
                        bgcolor: 'background.paper'
                      }
                    }}
                    onClick={handleFullscreen}
                  >
                    <FullscreenIcon />
                  </IconButton>
                </CardMedia>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Plan Details
                  </Typography>
                  
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Description
                    </Typography>
                    <Typography variant="body1">
                      {currentPlan.description}
                    </Typography>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: 'primary.light', 
                        color: 'white',
                        borderRadius: 2,
                        textAlign: 'center'
                      }}>
                        <StraightenIcon sx={{ mb: 1 }} />
                        <Typography variant="h4" sx={{ fontWeight: 600 }}>
                          {formatArea(currentPlan.total_sqft || currentPlan.total_area || 0)}
                        </Typography>
                        <Typography variant="body2">
                          Total Square Feet
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: 'secondary.light', 
                        color: 'white',
                        borderRadius: 2,
                        textAlign: 'center'
                      }}>
                        <HomeIcon sx={{ mb: 1 }} />
                        <Typography variant="h4" sx={{ fontWeight: 600 }}>
                          {currentPlan.units || currentPlan.unit_count || 0}
                        </Typography>
                        <Typography variant="body2">
                          Total Units
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  <Box sx={{ mt: 3 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Features
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip label="Modern Layout" size="small" />
                      <Chip label="Open Concept" size="small" />
                      <Chip label="Energy Efficient" size="small" />
                      <Chip label="Code Compliant" size="small" />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </Paper>

      {/* Fullscreen Dialog */}
      <Dialog
        open={!!fullscreenPlan}
        onClose={() => setFullscreenPlan(null)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh'
          }
        }}
      >
        {fullscreenPlan && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6">
                {getPlanTypeLabel(fullscreenPlan.type)} - {formatArea(fullscreenPlan.data.total_sqft || fullscreenPlan.data.total_area || 0)} sq ft
              </Typography>
              <IconButton onClick={() => setFullscreenPlan(null)}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
              <Box
                dangerouslySetInnerHTML={{ __html: fullscreenPlan.data.svg || fullscreenPlan.data.svg_layout || '' }}
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'grey.100',
                  '& svg': {
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain'
                  }
                }}
              />
            </DialogContent>
          </>
        )}
      </Dialog>
    </>
  );
};

export default FloorPlanViewer;